import { useEffect, useState } from "react";
import { CalendarDays, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const isUrl = (v) => !v || /^https?:\/\//i.test(v);

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function EventEditRow({ ev, onSaved }) {
  const [form, setForm] = useState({
    title: ev.title || "",
    description: ev.description || "",
    signup_url: ev.signup_url || "",
    watch_url: ev.watch_url || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const id = ev.event_id;

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
    if (!payload.title) return toast.error("Title can't be empty");
    if (!isUrl(payload.signup_url) || !isUrl(payload.watch_url)) return toast.error("Links must start with https://");
    setSaving(true);
    try {
      await api.patch(`/events/${id}`, payload);
      onSaved(id, payload);
      toast.success(`Saved ${payload.title}.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-800 rounded-sm p-4 space-y-3" data-testid={`admin-event-links-${id}`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        {new Date(ev.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
      </div>
      <Field label="Title">
        <input className="tavern-input w-full px-3 py-2 rounded-sm" maxLength={120} value={form.title}
          onChange={set("title")} data-testid={`admin-event-title-${id}`} />
      </Field>
      <Field label="Description">
        <textarea rows={3} maxLength={2000} className="tavern-input w-full px-3 py-2 rounded-sm" value={form.description}
          onChange={set("description")} data-testid={`admin-event-description-${id}`} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Sign Up link">
          <input className="tavern-input w-full px-3 py-2 rounded-sm" placeholder="https://..." value={form.signup_url}
            onChange={set("signup_url")} data-testid={`admin-signup-url-${id}`} />
        </Field>
        <Field label="Watch Event link">
          <input className="tavern-input w-full px-3 py-2 rounded-sm" placeholder="https://twitch.tv/..." value={form.watch_url}
            onChange={set("watch_url")} data-testid={`admin-watch-url-${id}`} />
        </Field>
      </div>
      <button onClick={save} disabled={saving} data-testid={`admin-save-event-links-${id}`}
        className="btn-crimson px-4 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 disabled:opacity-60">
        <Save size={14} /> {saving ? "Saving..." : "Save Event"}
      </button>
    </div>
  );
}

export function EventLinksSection() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([])); }, []);
  const onSaved = (id, patch) => setEvents((list) => list.map((e) => (e.event_id === id ? { ...e, ...patch } : e)));

  return (
    <section className="tavern-card p-6 sm:p-8" data-testid="admin-event-links-section">
      <div className="flex items-center gap-2 mb-2">
        <CalendarDays size={16} className="text-crimson-bright" />
        <h2 className="font-display tracking-[0.3em] uppercase text-sm text-white">Events</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        Edit the title, description and links for every event. New events from the Events page show up here automatically. Sign Up and Watch Event buttons appear only when a link is filled in.
      </p>
      {events === null ? (
        <div className="text-xs tracking-widest uppercase text-zinc-500 animate-pulse">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="text-sm text-zinc-500">No events yet. Create one on the Events page.</div>
      ) : (
        <div className="space-y-4">{events.map((ev) => <EventEditRow key={ev.event_id} ev={ev} onSaved={onSaved} />)}</div>
      )}
    </section>
  );
}
